import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useState } from 'react';
import getComplectations from '../../api/cars/getComplectations';
import createComplectation from '../../api/cars/createComplectation';
import Select from '../Select/Select';
import DictionaryNameModal from '../Modal/DictionaryNameModal';
import useDictionaryCreator from './useDictionaryCreator';

/**
 * @param {boolean} used — б/у-процесс: обычные комплектации плюс заведённые под
 * б/у, с возможностью завести новую прямо здесь.
 */
const SelectComplectations = ({
  handleSetSelect,
  handleSetState,
  style,
  params,
  dependantValue,
  dependencyAccessor = (obj) => obj,
  used,
  creatable,
  ...props
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const isFirstRun = useRef(true);
  // Список открыли раньше, чем определился контейнер: onFocus больше не сработает,
  // поэтому запоминаем намерение и догружаем, когда контейнер приедет.
  const awaitingContainer = useRef(false);
  // Контейнер, для которого список уже запрашивали.
  const loadedFor = useRef(null);
  const depKey = useMemo(() => dependencyAccessor(dependantValue), [dependantValue, dependencyAccessor]);

  // params бывает и не передан (см. Form.jsx), поэтому зовём его через проверку.
  const containerId = params ? params() : null;

  const load = useCallback(
    (id) => {
      // «Уже загружено» считаем по контейнеру, а не по длине списка: у нового
      // контейнера комплектаций ноль, и признак data.length > 0 не срабатывал
      // никогда. Select дёргает onFocus ещё и как onScrollToBottom после каждой
      // загрузки, поэтому пустой ответ уходил в бесконечный цикл запросов.
      if (loadedFor.current === id) return;
      loadedFor.current = id;
      setLoading(true);
      getComplectations({ containerId: id, used: used || undefined })
        .then((res) => {
          const complectations = res.data.map((item) => {
            return { value: item.id, label: item.title };
          });
          setData(complectations);
        })
        .catch(() => {
          // Дать шанс повторить при следующем открытии списка.
          loadedFor.current = null;
        })
        .finally(() => setLoading(false));
    },
    [used]
  );

  const onFocus = useCallback(() => {
    if (loading) return;
    if (!containerId) {
      awaitingContainer.current = true;
      return;
    }
    load(containerId);
  }, [containerId, loading, load]);

  // Контейнер приезжает асинхронно (ensureContainer по выбранной модели). Если
  // менеджер открыл комплектации быстрее ответа, список так и оставался пустым
  // до переоткрытия селекта.
  useEffect(() => {
    if (!containerId || !awaitingContainer.current) return;
    awaitingContainer.current = false;
    load(containerId);
  }, [containerId, load]);

  const create = useCallback(
    async (name) => {
      const containerId = params();
      if (!containerId) throw new Error('Сначала выберите марку и модель');
      // Характеристики (двигатель, привод, кузов) менеджер заполняет на самой
      // машине, поэтому комплектации хватает названия — остальное дефолты, как
      // на странице /cars/complectations/create.
      const res = await createComplectation({
        title: name,
        containerId,
        used: !!used,
        engineId: 1,
        carcaseId: 1,
        wdId: 2,
      });
      const id = Number(res?.data);
      if (!id) throw new Error('Не удалось добавить комплектацию');
      return { value: id, label: name };
    },
    [params, used]
  );

  const { extraOptions, selectProps, modalProps } = useDictionaryCreator({
    // Комплектация вешается на контейнер (марка+модель) — без него кнопки нет.
    enabled: Boolean((creatable ?? used) && containerId),
    kind: 'complectation',
    create,
    onChange: props?.onChange,
    onChangeCustom: props?.onChangeCustom,
    resetKey: depKey,
    // dependantValue — [марка, модель], см. dependency в carInfoInputs.
    context: depKey
      ? `Марка и модель: ${[].concat(dependantValue || []).map((o) => o?.label).filter(Boolean).join(' ')}`
      : undefined,
  });

  const options = useMemo(() => [...extraOptions, ...data], [extraOptions, data]);

  useEffect(() => {
    setData([]);
    awaitingContainer.current = false;
    loadedFor.current = null;
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (props?.onChange) {
      props.onChange(null);
    }
  }, [depKey])
  return (
    <>
      <Select
        handleSetSelect={(val) => handleSetSelect && handleSetSelect(val)}
        handleSetState={handleSetState}
        options={options}
        title={'Комплектация'}
        placeholder={'Выберите из списка'}
        onFocus={onFocus}
        style={style}
        isLoading={loading}
        {...props}
        {...selectProps}
      />
      {selectProps && <DictionaryNameModal {...modalProps} />}
    </>
  );
};

export default SelectComplectations;
